import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  RawAxiosRequestHeaders,
} from 'axios';
import { captureException } from '@sentry/react';
import { getAgentApiUrl, getApiUrl, getSourcingApiUrl } from '../util/environment';
import { emitHttpError, emitHttpTiming } from '../util/datadogDataLayer';
import { getForesightStore } from '../util/jotai-store';
import { REQUEST_ERROR_TYPE } from '../util/SentryUtils';
import { clearBootCache } from './boot-cache/bootCache';
import {
  readAccessToken,
  readCustomerTenantId,
  sessionExpiredState,
  whenUserLoaded,
} from './state/AuthState';

declare module 'axios' {
  // Per-request opt-out for Sentry capture on listed status codes — used when a
  // status (e.g. 409) is part of the API contract and the caller handles it
  // gracefully, so it shouldn't pollute error monitoring.
  export interface AxiosRequestConfig {
    skipSentryStatuses?: number[];
    /** Set on the one retry a 401 earns when the token has been renewed since the request went out. */
    retriedWithRenewedToken?: boolean;
  }
}

export interface IMaggieAxiosHttpConf {
  baseURL: string;
  on401Callback?: (response: AxiosResponse) => void;
  getToken: () => Promise<string | undefined>;
  /**
   * Resolves once the session's token is settled. A 401 waits for it, then retries once if the token has
   * changed since the request went out; requests can start before Frontegg's user object arrives.
   */
  whenAuthSettled?: () => Promise<void>;
  /** Customer tenant to name on each request, when known. */
  getTenantId?: () => string | undefined;
  /** Default request timeout (ms) for this instance. Per-call `timeout` in the
   *  request config still overrides it. */
  timeout?: number;
}

/** Default client-side ceiling on the `maggie` API instance. A request that
 *  outlives this rejects locally instead of hanging until the browser/proxy
 *  gives up — long endpoints (e.g. compareSOI) raise it per-call. */
const MAGGIE_DEFAULT_TIMEOUT_MS = 60_000;

export class MaggieAxiosHttp {
  private readonly config: IMaggieAxiosHttpConf;

  private axiosInstance: AxiosInstance;

  constructor(config: IMaggieAxiosHttpConf) {
    this.config = config;
    this.axiosInstance = this.initAxios();
  }

  async delete<ResponseType, RequestType = undefined>(
    url: string,
    config: AxiosRequestConfig<RequestType> | undefined = undefined
  ): Promise<AxiosResponse<ResponseType>> {
    return this.axiosInstance.delete<ResponseType, AxiosResponse<ResponseType>>(url, config);
  }

  async get<ResponseType>(
    url: string,
    config: AxiosRequestConfig<null> | undefined = undefined
  ): Promise<AxiosResponse<ResponseType>> {
    return this.axiosInstance.get<ResponseType, AxiosResponse<ResponseType>, null>(url, config);
  }

  async post<RequestType, ResponseType>(
    url: string,
    data?: RequestType,
    config?: AxiosRequestConfig<RequestType>
  ): Promise<AxiosResponse<ResponseType>> {
    return this.axiosInstance.post<ResponseType, AxiosResponse<ResponseType>, RequestType>(url, data, config);
  }

  async put<RequestType, ResponseType>(
    url: string,
    data?: RequestType,
    config?: AxiosRequestConfig<RequestType>
  ): Promise<AxiosResponse<ResponseType>> {
    return this.axiosInstance.put<ResponseType, AxiosResponse<ResponseType>, RequestType>(url, data, config);
  }

  async patch<RequestType, ResponseType>(
    url: string,
    data?: RequestType,
    config?: AxiosRequestConfig<RequestType>
  ): Promise<AxiosResponse<ResponseType>> {
    return this.axiosInstance.patch<ResponseType, AxiosResponse<ResponseType>, RequestType>(
      url,
      data,
      config
    );
  }

  // A request sent on a token that has since been renewed earns one retry on the new token. That covers a
  // 401 that lands while Frontegg is still loading the user, which is not an expired session.
  private async retriesWithRenewedToken(error: AxiosError): Promise<boolean> {
    const { getToken, whenAuthSettled } = this.config;
    const requestConfig = error.config;
    if (
      error.response?.status !== 401 ||
      !requestConfig ||
      requestConfig.retriedWithRenewedToken ||
      !whenAuthSettled
    ) {
      return false;
    }
    await whenAuthSettled();
    const sentToken = String(requestConfig.headers?.Authorization ?? '').replace(/^Bearer /, '');
    const currentToken = (await getToken()) ?? '';
    return currentToken !== '' && currentToken !== sentToken;
  }

  private initAxios(): AxiosInstance {
    const { baseURL, on401Callback, getToken, getTenantId, timeout } = this.config;
    const axiosInstance = axios.create({
      baseURL: baseURL,
      adapter: 'fetch',
      // Omitted (agent/sourcing) means no default timeout, preserving today's
      // unbounded behavior for those instances.
      ...(timeout !== undefined ? { timeout } : {}),
    });

    axiosInstance.interceptors.request.use(async (config) => {
      if (!getToken) {
        throw new Error('No token provided');
      }
      config.headers = {
        ...((config.headers || {}) as RawAxiosRequestHeaders),
        Authorization: `Bearer ${(await getToken()) ?? ''}`,
        // axios 1.16+ fetch adapter injects `User-Agent: axios/<version>`, a forbidden
        // header Safari leaks into the CORS preflight (which the API rejects). `false`
        // skips the adapter's set-if-absent write and is dropped at serialization, so it
        // never ships. Must be `false`, not delete(): the adapter sets it post-interceptor.
        'User-Agent': false,
      } as AxiosRequestHeaders;

      const tenantId = getTenantId?.();
      if (tenantId) {
        config.headers['X-Tenant-ID'] = tenantId;
      }

      // Stamp the start time for Datadog `http.request` timing (metrics-only).
      config.metadata = { startTime: performance.now() };

      return config;
    });
    axiosInstance.interceptors.response.use(
      (response) => {
        emitHttpTiming(response);
        return response;
      },
      async (error) => {
        if (await this.retriesWithRenewedToken(error)) {
          return axiosInstance.request({ ...error.config, retriedWithRenewedToken: true });
        }

        // Datadog metric — emitHttpError is wrapped internally, so a throw here
        // can never pre-empt the Sentry capture / on401 / rejection below.
        emitHttpError(error);

        // Extract once and reuse across the Sentry branches + the 401 check.
        // Sentry keeps the raw url for debugging; Datadog scrubs independently.
        const method = error?.config?.method?.toUpperCase();
        const url = error?.config?.url;
        const status = error?.response?.status;

        if (error?.response) {
          const { statusText, data } = error.response;
          const { skipSentryStatuses } = error.config ?? {};

          if (!skipSentryStatuses?.includes(status)) {
            captureException(error, {
              tags: {
                type: REQUEST_ERROR_TYPE.http,
                method,
                status: status?.toString(),
              },
              extra: {
                url,
                statusText,
                responseData: data,
                requestData: error.config?.data,
              },
            });
          }
        } else if (error?.request) {
          captureException(error, {
            tags: {
              type: REQUEST_ERROR_TYPE.network,
            },
            extra: {
              url,
              method,
            },
          });
        } else {
          captureException(error, {
            tags: {
              type: REQUEST_ERROR_TYPE.axios,
            },
            extra: {
              message: error.message,
            },
          });
        }

        if (status === 401) {
          // FIXME: Atm we are not getting the 401 see MAGGIE-2853
          on401Callback?.(error);
        }
        return Promise.reject(error);
      }
    );

    return axiosInstance;
  }

  getAxiosInstance() {
    return this.axiosInstance;
  }
}

export class MaggieHttp {
  static maggie: MaggieAxiosHttp;
  static agent: MaggieAxiosHttp;
  static sourcing: MaggieAxiosHttp;

  /**
   * Points the API clients at the store's session: its token (early or the user's), its expired-session flag
   * on a 401, and its user load for the renewed-token retry. Once per page; the early boot and the app's
   * own start both call it.
   */
  static initForSession(store = getForesightStore()) {
    if (this.maggie) {
      return;
    }
    this.init({
      getToken: async () => readAccessToken(store),
      on401Callback: () => {
        store.set(sessionExpiredState, true);
        // The session-expired dialog signs in again without logout(), which is what clears it otherwise.
        void clearBootCache();
      },
      whenAuthSettled: () => whenUserLoaded(store),
    });
  }

  static init(config: Omit<IMaggieAxiosHttpConf, 'baseURL' | 'getTenantId'>) {
    this.maggie = new MaggieAxiosHttp({
      baseURL: getApiUrl(),
      timeout: MAGGIE_DEFAULT_TIMEOUT_MS,
      ...config,
    });

    // The assistant is shared across customers and routes by the customer
    // tenant; the other instances are already per-customer deployments
    this.agent = new MaggieAxiosHttp({
      baseURL: getAgentApiUrl(),
      getTenantId: readCustomerTenantId,
      ...config,
    });

    this.sourcing = new MaggieAxiosHttp({
      baseURL: getSourcingApiUrl(),
      ...config,
    });
  }
}
